import { Component, Prop, h, Event, EventEmitter, Watch, State } from '@stencil/core';
import { isIOS } from '../../../../util';
@Component({
  tag: 'zeudy-youtube',
  styleUrl: 'zeudy-youtube.css',
  shadow: false,
})
export class zeudyYoutube {
  @Prop() src: string;
  @Prop({ mutable: true }) poster: string;
  @Prop({ mutable: true }) lazyLoad: boolean;
  @Prop() player: any;
  @Prop({ mutable: true }) getRef?: (elm?: HTMLIFrameElement | HTMLVideoElement | HTMLDivElement) => void;

  /**
   * Events
   */
  @Event() reload: EventEmitter<string>;

  /**
   * State
   */
  @State() reloadPlayer: boolean;
  @State() isWebView: boolean;

  /**
   * When player is set, do ratio and fixes
   * @returns
   */
  @Watch('player')
  handlePlayerChange() {
    if (!this.player) {
      return;
    }

    this.fixes();
    this.setPoster();
  }

  // fixes issue where youtube can sometimes can be muted if played before load
  fixes() {
    this.player.once('statechange', e => {
      // only playing
      if (e.detail.code !== 1) {
        return;
      }
      // not autoplay
      if (e?.detail?.plyr?.config?.blockAttributes?.mutedPreview?.enabled) {
        return;
      }
      // unmute
      this.player.muted = false;
    });
  }

  // get id from youtube url
  getId(url) {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = (url || '').match(regExp);
    return match && match?.[2]?.length === 11 ? match[2] : null;
  }

  // load player
  loadPlayer() {
    this.lazyLoad = false;
    this.reloadPlayer = true;
  }

  // wait for component to update before reloading
  componentDidRender() {
    if (this.reloadPlayer) {
      this.reloadPlayer = false;
      this.reload.emit('play');
    }
  }

  /**
   * detect if we're in a webview browser
   */
  setWebView() {
    // @ts-ignore
    let standalone = window.navigator?.standalone,
      userAgent = window.navigator.userAgent.toLowerCase(),
      safari = /safari/.test(userAgent),
      ios = /iphone|ipod|ipad/.test(userAgent);

    if (ios) {
      // ios webview
      this.isWebView = !standalone && !safari;
    } else {
      // android webview
      this.isWebView = userAgent.includes('wv');
    }
  }

  // Load image avoiding xhr/fetch CORS issues. 
  // Server status can't be obtained this way unfortunately, so this uses "naturalWidth" to determine if the image has loaded/
  loadImage(src, minWidth = 1) {
    return new Promise((resolve, reject) => {
      const image = new Image();
  
      const handler = () => {
        delete image.onload;
        delete image.onerror;
        (image.naturalWidth >= minWidth ? resolve : reject)(src);
      };
  
      Object.assign(image, { onload: handler, onerror: handler, src });
    });
  }

  setPoster() {
    if (!this.player.poster) {
      let posterSrc = (s) => `https://i.ytimg.com/vi/${this.getId(this.src)}/${s}default.jpg`;

      this.loadImage(posterSrc('maxres'), 121) // 480p padded 4:3
        .catch(() => this.loadImage(posterSrc('sd'), 121)) // 360p padded 4:3. Always exists
        .catch(() => this.loadImage(posterSrc('hq'), 121)) // 360p padded 4:3. Always exists
        .then((image:string) => this.player.poster = image )
        .catch(() => {});
    }
  }

  // don't lazy load on iOS
  componentWillLoad() {
    this.setWebView();
    if (this.lazyLoad && isIOS()) {
      this.lazyLoad = false;
    }
  }

  render() {
    if (this.isWebView) {
      return (
        <div class="fallback-container">
          <iframe src={this.src} allowFullScreen allowtransparency allow="autoplay"></iframe>
        </div>
      );
    }

    if (this.lazyLoad) {
      return (
        <div>
          <zeudy-video part="video" getRef={this.getRef} src="" provider="youtube"></zeudy-video>
          <div class="zeudy-player__play-cover" onClick={() => this.loadPlayer()}></div>
        </div>
      );
    }

    return <div class="plyr__video-embed" part="embed" ref={this.getRef} data-plyr-provider="youtube" data-plyr-embed-id={this.getId(this.src)}></div>;
  }
}
